import styled, { keyframes, css } from 'styled-components'

/* ---- animations (all disabled under prefers-reduced-motion) ---- */

const floatPulse = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-4px) scale(1.03); }
`

const ring = keyframes`
  0% { transform: scale(0.9); opacity: 0.5; }
  70% { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1.5); opacity: 0; }
`

const dots = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`

const reveal = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`

/* ---- shell ---- */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: var(--careco-bg, #f5f8f7);
  overflow-y: auto;
  font-family: var(--careco-font, 'Lato', sans-serif);
  color: var(--careco-text-primary);
`

export const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 62px;
  padding: 0 28px;
  background: var(--careco-surface);
  border-bottom: 1px solid var(--careco-border);

  @media (max-width: 900px) {
    padding: 0 16px;
  }
`

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  padding: 8px 10px;
  border-radius: var(--careco-rounded-sm, 8px);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--careco-text-secondary);
  cursor: pointer;
  &:hover {
    background: var(--careco-row-default);
    color: var(--careco-text-primary);
  }
`

export const TopMeta = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
`

export const TopEyebrow = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--careco-text-muted);
`

export const TopTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--careco-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const Layout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 28px;
  max-width: 1360px;
  margin: 0 auto;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding: 24px;
    gap: 24px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
`

/* ---- sidebar ---- */

export const Sidebar = styled.aside`
  flex: 0 0 300px;
  position: sticky;
  top: 90px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (max-width: 1100px) {
    flex-basis: 260px;
  }

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    max-width: 100%;
    position: static;
  }
`

export const SideCard = styled.div`
  min-width: 0;
  background: var(--careco-surface);
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-lg, 20px);
  box-shadow: var(--careco-shadow-card, 0 2px 10px 2px rgba(110, 110, 110, 0.06));
  padding: 16px;
`

export const SideLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--careco-text-muted);
  margin-bottom: 12px;
`

export const FileChip = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  padding: 10px 12px;
  border-radius: var(--careco-rounded-sm, 8px);
  border: 1px solid ${({ active }) => (active ? 'var(--careco-primary)' : 'var(--careco-border)')};
  background: ${({ active }) => (active ? 'var(--careco-primary-light)' : 'var(--careco-surface)')};
  color: var(--careco-text-primary);
  font-size: 13px;
  font-weight: 500;
  & svg {
    flex-shrink: 0;
    color: var(--careco-primary);
  }
  & span {
    min-width: 0;
    flex: 1 1 0%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

/* A scrollable column of care-plan chips — the audit reviews every plan, so all
   of them are listed here and the list scrolls once there are more than a few. */
export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-height: 264px;
  overflow-y: auto;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--careco-border);
    border-radius: 999px;
  }
`

export const SideCount = styled.span`
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--careco-primary-light);
  color: var(--careco-primary);
  font-size: 11px;
  font-weight: 700;
  vertical-align: middle;
`

export const RunButton = styled.button`
  width: 100%;
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-primary);
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--careco-primary-hover);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
  }
`

// The tooltip anchors to this box, so the button's top margin lives here instead.
export const RunButtonTipAnchor = styled.span`
  display: block;
  width: 100%;
  margin-top: 12px;
  & > ${RunButton} {
    margin-top: 0;
  }
`

/**
 * Secondary action under the primary one. The finished PDF is downloaded from
 * here rather than from the table, because once the report is on screen this is
 * where somebody is standing when they decide they want to keep it.
 */
export const DownloadButton = styled.button`
  width: 100%;
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-surface);
  color: var(--careco-text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: var(--careco-primary);
    color: var(--careco-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: 2px;
  }
`

export const ReviewItem = styled.div`
  padding: 10px 12px;
  border-radius: var(--careco-rounded-sm, 8px);
  border: 1px solid var(--careco-border);
  font-size: 13px;
  & b {
    display: block;
    font-weight: 600;
    color: var(--careco-text-primary);
  }
  & small {
    color: var(--careco-text-muted);
    font-size: 12px;
  }
`

/* ---- main ---- */

export const Main = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`

/* ---- processing indicator (sits above the blurred content, integrated) ---- */

export const ProcInline = styled.div<{ visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin-bottom: 16px;
  border-radius: var(--careco-rounded-md, 12px);
  /* subtle green-tinted glass */
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.7), rgba(228, 239, 237, 0.55));
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(15, 61, 60, 0.1);
  box-shadow: 0 6px 22px rgba(15, 61, 60, 0.07);
  overflow: hidden;
  transition: opacity 400ms ease, transform 400ms ease, margin 400ms ease, max-height 400ms ease;
  ${noMotion};
  ${({ visible }) =>
    !visible &&
    css`
      opacity: 0;
      transform: translateY(-6px);
      max-height: 0;
      padding-top: 0;
      padding-bottom: 0;
      margin-bottom: 0;
      border-color: transparent;
      box-shadow: none;
      pointer-events: none;
    `}
`

export const ProcOrb = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--careco-primary);
  background: var(--careco-primary-light);
  animation: ${floatPulse} 2.6s ease-in-out infinite;
  ${noMotion};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid var(--careco-primary);
    opacity: 0;
    animation: ${ring} 2.6s ease-out infinite;
    ${noMotion};
  }
`

export const ProcTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const ProcTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--careco-text-primary);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  & i {
    font-style: normal;
    animation: ${dots} 1.4s infinite;
    ${noMotion};
  }
  & i:nth-child(2) {
    animation-delay: 0.2s;
  }
  & i:nth-child(3) {
    animation-delay: 0.4s;
  }
`

export const ProcSub = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: var(--careco-text-secondary);
`

/* ---- ready to audit (nothing has been run yet) ---- */

/**
 * The main column before an audit exists.
 *
 * Opening this screen no longer starts a job, so the column would otherwise be
 * blank until somebody acted. The checklist and the ready card below it carry
 * the decision instead — what will be reviewed, and the single control that
 * starts it. The sidebar's run button is hidden while this is showing so there
 * is only one primary action on the page.
 */
export const ReadyArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  animation: ${reveal} 420ms ease both;
  ${noMotion};
`

export const ReadyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  /* A soft brand halo fades out over the top of the card, behind the orb —
     enough to make the card feel like the start of something, not a blank
     panel. Solid surface fallback for engines that skip the gradient. */
  background: var(--careco-surface);
  background:
    radial-gradient(130% 140px at 50% 0, var(--careco-primary-light) 0%, transparent 72%),
    var(--careco-surface);
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-lg, 20px);
  box-shadow: var(--careco-shadow-card, 0 2px 10px 2px rgba(110, 110, 110, 0.06));
  padding: 44px 32px 40px;
`

export const ReadyOrb = styled.span`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--careco-primary);
  background: var(--careco-primary-light);
  box-shadow: 0 0 0 8px rgba(15, 61, 60, 0.05), 0 10px 24px rgba(15, 61, 60, 0.12);
  animation: ${floatPulse} 3.6s ease-in-out infinite;
  ${noMotion};
`

export const ReadyTitle = styled.h1`
  margin: 20px 0 0;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--careco-text-primary);
  overflow-wrap: anywhere;
`

export const ReadyText = styled.p`
  margin: 10px 0 0;
  max-width: 540px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--careco-text-secondary);
`

export const ReadyCta = styled.button`
  margin-top: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 48px;
  padding: 0 30px;
  border: none;
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-primary);
  background: linear-gradient(135deg, var(--careco-primary) 0%, var(--careco-primary-hover) 100%);
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(15, 61, 60, 0.2);
  transition: box-shadow 180ms ease, transform 160ms ease;
  ${noMotion};
  &:hover:not(:disabled) {
    box-shadow: 0 12px 30px rgba(15, 61, 60, 0.28);
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: 3px;
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
    box-shadow: none;
    transform: none;
    pointer-events: none;
  }
`

// The tooltip anchors to this box, so the CTA's top margin lives here instead.
export const ReadyCtaTipAnchor = styled.span`
  display: inline-block;
  margin-top: 26px;
  & > ${ReadyCta} {
    margin-top: 0;
  }
`

export const ReadyBlocked = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--careco-status-inactive, #b8860b);
`

export const ChecksCard = styled.div`
  background: var(--careco-surface);
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-lg, 20px);
  box-shadow: var(--careco-shadow-card, 0 2px 10px 2px rgba(110, 110, 110, 0.06));
  padding: 22px 24px;
`

export const ChecksTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--careco-text-primary);
`

export const ChecksSub = styled.p`
  margin: 4px 0 18px;
  font-size: 13px;
  color: var(--careco-text-muted);
`

export const ChecksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-row-default);
  font-size: 13.5px;
  line-height: 1.35;
  color: var(--careco-text-secondary);
  transition: background 140ms ease, border-color 140ms ease;
  & svg {
    flex-shrink: 0;
  }
  &:hover {
    background: var(--careco-surface);
    border-color: var(--careco-border);
  }
`

/** The check glyph sits in a small brand-tinted disc rather than floating loose. */
export const CheckDot = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--careco-primary-light);
  color: var(--careco-primary);
`

/* ---- failure (same slot as the processing indicator) ---- */

/**
 * A failed audit takes the processing indicator's place rather than closing the
 * overlay. By the time a job fails somebody is already looking at this screen,
 * and dropping them back to the table with only a toast to explain it reads as
 * the screen having crashed.
 */
export const ErrorInline = styled(ProcInline)`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.7), rgba(251, 231, 231, 0.55));
  border-color: rgba(186, 0, 0, 0.18);
  box-shadow: 0 6px 22px rgba(186, 0, 0, 0.07);
`

export const ErrorOrb = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--careco-error, #ba0000);
  background: var(--careco-error-light, #fbe7e7);
`

export const ErrorTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--careco-text-primary);
`

/* ---- the content area that blurs while processing ---- */

export const SectionsArea = styled.div`
  position: relative;
`

export const Sections = styled.div<{ processing: boolean }>`
  transition: filter 500ms ease, opacity 500ms ease;
  ${noMotion};
  ${({ processing }) =>
    processing &&
    css`
      filter: blur(5px);
      opacity: 0.8;
      pointer-events: none;
      user-select: none;
    `}
`

/* Soft translucent glass over the blurred content. */
export const GlassVeil = styled.div<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: var(--careco-rounded-lg, 20px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), rgba(228, 239, 237, 0.32));
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  transition: opacity 500ms ease;
  ${noMotion};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
`

/* ---- completed report — one document, not a stack of cards ---- */

export const RevealItem = styled.div<{ index: number }>`
  animation: ${reveal} 460ms ease both;
  animation-delay: ${({ index }) => Math.min(index, 8) * 70}ms;
  ${noMotion};
`

/**
 * The whole review is one surface now. Sections are separated by hairlines
 * inside it rather than by gaps between free-floating cards, so it reads as a
 * generated document with a beginning and an end.
 */
export const ReportDoc = styled.div`
  background: var(--careco-surface);
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-lg, 20px);
  box-shadow: var(--careco-shadow-card, 0 2px 10px 2px rgba(110, 110, 110, 0.06));
  overflow: hidden;
  animation: ${reveal} 420ms ease both;
  ${noMotion};
`

export const DocHeader = styled.header`
  position: relative;
  padding: 26px 30px 22px;
  border-bottom: 1px solid var(--careco-border);
  background: linear-gradient(180deg, var(--careco-primary-light) 0%, transparent 92px), var(--careco-surface);

  @media (max-width: 900px) {
    padding: 20px 16px;
  }
`

/** Top-right of DocHeader — absolutely placed so eyebrow/title/meta keep flow. */
export const CopyButton = styled.button`
  position: absolute;
  top: 22px;
  right: 26px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: var(--careco-rounded-sm, 8px);
  border: 1px solid var(--careco-border);
  background: var(--careco-surface);
  color: var(--careco-text-secondary);
  font-family: var(--careco-font, 'Lato', sans-serif);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  & svg {
    display: block;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover {
    background: var(--careco-primary-light);
    border-color: var(--careco-primary);
    color: var(--careco-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: 2px;
  }

  @media (max-width: 900px) {
    top: 16px;
    right: 12px;
  }
`

export const DocEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--careco-primary);
  & svg {
    flex-shrink: 0;
  }
`

export const DocTitle = styled.h1`
  margin: 10px 0 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--careco-text-primary);
  overflow-wrap: anywhere;

  @media (max-width: 900px) {
    /* Clear absolute Copy button */
    padding-right: 88px;
    font-size: 20px;
  }
`

export const DocMeta = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--careco-text-muted);
  overflow-wrap: anywhere;
`

export const DocScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  max-width: 460px;
`

export const DocScoreLabel = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--careco-text-secondary);
`

export const ScoreTrack = styled.div`
  flex: 1 1 auto;
  height: 8px;
  border-radius: 999px;
  background: var(--careco-divider);
  overflow: hidden;
`

export const ScoreFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  border-radius: inherit;
  background: var(--careco-primary);
  transition: width 900ms ease;
  ${noMotion};
`

export const ScoreValue = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--careco-primary);
  font-variant-numeric: tabular-nums;
`

export const DocBody = styled.div`
  padding: 6px 30px 26px;

  @media (max-width: 900px) {
    padding: 6px 16px 20px;
  }
`

export const DocSection = styled.section<{ tone?: 'alert' }>`
  padding: 22px 0;
  border-top: 1px solid var(--careco-divider);
  &:first-child {
    border-top: none;
  }
  ${({ tone }) =>
    tone === 'alert' &&
    css`
      margin: 14px -30px 0;
      padding: 20px 30px;
      background: var(--careco-error-light, #fbe7e7);
      border-top: 1px solid rgba(186, 0, 0, 0.16);
      border-bottom: 1px solid rgba(186, 0, 0, 0.16);

      @media (max-width: 900px) {
        margin: 14px -16px 0;
        padding: 16px;
      }
    `}
`

export const DocSectionTitle = styled.h2<{ tone?: 'alert' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  font-size: 15.5px;
  font-weight: 700;
  color: ${({ tone }) => (tone === 'alert' ? 'var(--careco-error, #ba0000)' : 'var(--careco-text-primary)')};
  & svg {
    flex-shrink: 0;
  }
  ${({ tone }) =>
    tone !== 'alert' &&
    css`
      &::before {
        content: '';
        width: 3px;
        height: 15px;
        border-radius: 2px;
        background: var(--careco-primary);
        flex-shrink: 0;
      }
    `}
`

export const Block = styled.div`
  & + & {
    margin-top: 16px;
  }
`

export const BlockHead = styled.div<{ tone?: 'good' | 'improve' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
  color: ${({ tone }) => (tone === 'improve' ? 'var(--careco-status-inactive)' : 'var(--careco-primary)')};
  & svg {
    flex-shrink: 0;
  }
`

export const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Item = styled.li<{ tone?: 'alert' }>`
  position: relative;
  padding-left: 18px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--careco-text-secondary);
  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 8px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ tone }) => (tone === 'alert' ? 'var(--careco-error, #ba0000)' : 'var(--careco-text-muted)')};
  }
`

export const DocFootnote = styled.p`
  margin: 20px 0 0;
  padding: 14px 16px;
  border-radius: var(--careco-rounded-md, 12px);
  background: var(--careco-row-default);
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--careco-text-secondary);
`

export const DocSignoff = styled.p`
  margin: 14px 0 0;
  font-size: 12px;
  font-style: italic;
  color: var(--careco-text-muted);
`

/* ---- version history switcher (sidebar) ---- */

export const VersionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* 10 VersionItems + 9 gaps — scroll when there are more than 10 */
  max-height: calc(10 * (20px + 2px + 1.25em + 1.2em) + 9 * 6px);
  overflow-y: auto;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--careco-border);
    border-radius: 999px;
  }
`

export const VersionItem = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: var(--careco-rounded-sm, 8px);
  border: 1px solid ${({ selected }) => (selected ? 'var(--careco-primary)' : 'var(--careco-border)')};
  background: ${({ selected }) => (selected ? 'var(--careco-primary-light)' : 'var(--careco-surface)')};
  font-family: inherit;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease;
  &:hover {
    border-color: var(--careco-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: 2px;
  }
`

export const VersionName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: var(--careco-text-primary);
`

export const VersionMeta = styled.span`
  font-size: 12px;
  color: var(--careco-text-muted);
`
